import { useEffect, useRef, useState } from 'react'
import { Input } from '@nextui-org/react'
import * as Sentry from '@sentry/nextjs'
import { Box, Text, Flex, Spinner } from 'theme-ui'
import CustomCard from '~/components/CustomCard'
import CardHeader from '../CardHeader'
import KlaviyoFormModal from '~/components/KlaviyoFormModal'
import { useAnalytics, useCart } from '@chordcommerce/react-autonomy'
import { loadStripe } from '@stripe/stripe-js'
import VariantOptions from '../Checkout/VariantOptions'
import { useCheckout } from '~/context/checkout'
import { useTranslate } from '~/hooks/use-translate'
import { Button } from '@propertylens/ui-kit'
import {
  URL_PROMO_CODES_INITIAL,
  URL_PROMO_CODES_OUTSTANDING,
} from '~/utils/constants'
import { toUsdCurrency } from '~/utils/formatters'
import { getStoredUTMParameters, getTimeDifferenceInDays } from '~/utils/utils'
import { CHECKOUT_STEP_CHECKOUT_COMPLETE } from '~/utils/constants'
import { getParam } from '~/utils/url'
import { useIsPreviewing } from '@builder.io/react'
import applyURLPromoCodes from '~/hooks/applyUrlPromos'
const PROPERTYLENS_APP_URL = process.env.NEXT_PUBLIC_PROPERTYLENS_APP_URL
const CHORD_OMS_DEFAULT_CHANNEL =
  process.env.NEXT_PUBLIC_CHORD_OMS_DEFAULT_CHANNEL
const GOOGLE_TAGS_CONVERSION_PIXEL =
  process.env.NEXT_PUBLIC_GOOGLE_TAGS_CONVERSION_PIXEL

// Only call loadStripe() outside of a component’s render to avoid recreating
// the Stripe object on every render.
const stripePromise = loadStripe(process.env.STRIPE_PK_KEY, {
  stripeAccount: process.env.STRIPE_CONNECTED_ACCOUNT,
})

const CheckoutCheckoutForm = ({
  variantOptions,
  showHeaderCart,
  bundleText,
  showProductDescription,
  showPromoCode,
  customProductText,
  klaviyoSampleFormCtaText,
  klaviyoSampleFormId,
  customRadioLabel,
  redirectParameters,
}) => {
  const isPreviewing = useIsPreviewing()
  const { chordAnalytics, trackCartViewed, trackProductViewed } = useAnalytics()
  const { addToCart, cart, emptyCart, addPromoCode, modifyCart, forgetCart } =
    useCart()
  const { prepareCheckout, setStep, finalizeCheckout } = useCheckout()
  const paymentElementRef = useRef(null)
  const [apiError, setApiError] = useState(null)
  const [checkout, setCheckout] = useState(null)
  const [isCartLoading, setIsCartLoading] = useState(false)
  const [price, setPrice] = useState(null)
  const [quantity, setQuantity] = useState(null)
  const [selectedSku, setSelectedSku] = useState(
    variantOptions?.[0]?.sku || cart.lineItems?.[0]?.variant?.sku
  )
  const [isCouponCodeLoading, setIsCouponCodeLoading] = useState(false)
  const orderNumber = { token: cart.token, number: cart.number }
  const [inputPromoCodeValue, setInputPromoCodeValue] = useState('')
  const translate = useTranslate()
  const variant = variantOptions?.find((v) => v.sku === selectedSku)
  const { channel, lineItems, total, checkoutSessionId } = cart || {}

  const affiliateCodeParam = getParam('affiliatecode')
  const affiliateNameParam = getParam('affiliatename')
  const promoCodeInputDisabled = !!getParam('affiliatecode')

  const utmParams = getStoredUTMParameters()

  useEffect(() => {
    if (affiliateCodeParam) {
      setInputPromoCodeValue(affiliateCodeParam)
    }
  }, [affiliateCodeParam])

  const handleCheckout = async () => {
    if (checkout) {
      checkout.destroy()
    }
    // if (event) event.preventDefault()
    setIsCartLoading(true)
    setApiError(null)

    try {
      if (inputPromoCodeValue) {
        try {
          await addPromoCode({ promoCode: inputPromoCodeValue })
        } catch (error) {
          Sentry.captureException(error)
          if (
            error?.message &&
            error.message ===
              'The coupon code has already been applied to this order'
          ) {
            // Do nothing and continue to next line of code... const cart = await prepareCheckout();
          } else if (error?.message) {
            switch (error.message) {
              case "The coupon code you entered doesn't exist. Please try again.":
              case 'The coupon code is expired':
              case 'Coupon code usage limit exceeded':
              case 'This coupon code can only be used once per user.':
              case 'This coupon code can only be applied to your first order.':
              case 'You need to login before applying this coupon code.':
                setApiError(error.message)
                setIsCouponCodeLoading(false)
                setIsCartLoading(false)
                break

              case 'You need to add an applicable variant before applying this coupon code.':
                setApiError(
                  'This coupon code is not valid for the selected product quantity. Please update your selection or try another code.'
                )
                setIsCartLoading(false)
                setIsCouponCodeLoading(false)
                break
              default:
                setApiError('We could not apply the coupon. Please try again.')
                setIsCartLoading(false)
                setIsCouponCodeLoading(false)
            }
          }
        }
      }
      await applyURLPromoCodes(addPromoCode)
      const cart = await prepareCheckout()

      const stripe = await stripePromise

      const checkoutObj = await stripe.initEmbeddedCheckout({
        clientSecret: cart?.clientSecret,
        onComplete: () => handleComplete(checkoutObj, cart),
      })

      try {
        window.fbq('track', 'AddtoCart', {
          value: cart.total,
          currency: 'USD',
          content_ids: cart.lineItems[0].variant.sku,
          content_type: cart.lineItems[0].variant.name,
          transaction_id: cart.number,
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_term: utmParams.utm_term,
          utm_content: utmParams.utm_content,
        })
        window.rdt('track', 'AddToCart', {
          value: cart.total,
          currency: 'USD',
          content_ids: cart.lineItems[0].variant.sku,
          content_type: cart.lineItems[0].variant.name,
          transaction_id: cart.number,
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_term: utmParams.utm_term,
          utm_content: utmParams.utm_content,
        })
      } catch (error) {
        Sentry.captureException(error)
      }

      setCheckout(checkoutObj)
      checkoutObj.mount('#checkout')
      trackCartViewed({
        products: [
          {
            variant: variant,
          },
        ],
      })
    } catch (error) {
      Sentry.captureException(error)
      setApiError(translate('error_api_default'))
    }

    setIsCartLoading(false)
    setIsCouponCodeLoading(false)
  }

  const handleComplete = async (checkoutObj, cart) => {
    await chordAnalytics.trackOrderCompleted({
      order: cart,
      products: cart.lineItems.map((lineItem) => lineItem.variant),
    })
    try {
      window.gtag('event', 'conversion', {
        send_to: GOOGLE_TAGS_CONVERSION_PIXEL,
        value: 1.0,
        currency: 'USD',
        transaction_id: '',
      })
    } catch (error) {
      Sentry.captureException(error)
    }
    checkoutObj.unmount()
    // event tracking for GTM
    if (window?.dataLayer) {
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          currency: 'USD',
          value: cart.total,
          transaction_id: cart.number,
          coupon: cart.promoCodes?.[0]?.code,
          tax: cart.taxTotal,
          total_amount_after_credit: cart.orderTotalAfterStoreCredit,
          items: cart.lineItems.map((lineItem) => ({
            item_id: lineItem.variant.sku,
            item_name: lineItem.variant.name,
            price: lineItem.variant.price,
            quantity: lineItem.quantity,
          })),
          utm_content: utmParams.utm_content,
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_term: utmParams.utm_term,
        },
      })
    }
    localStorage.removeItem(URL_PROMO_CODES_INITIAL)
    localStorage.removeItem(URL_PROMO_CODES_OUTSTANDING)
    setStep(CHECKOUT_STEP_CHECKOUT_COMPLETE)
    try {
      // @ts-ignore - Chord SDK doesnt provide types
      const userFinalizedData = await finalizeCheckout({
        orderNumber,
        checkoutSessionId,
      })
      const handoffParams = new URLSearchParams({
        email: userFinalizedData.email,
        intent: 'DIRECTCHECKOUT',
      })
      if (redirectParameters) {
        redirectParameters.forEach((paramObject) => {
          handoffParams.append(
            paramObject.parameterName,
            paramObject.parameterValue
          )
        })
      }
      if (affiliateCodeParam) {
        if (handoffParams.get('affiliatecode')) {
          handoffParams.delete('affiliatecode')
        }
        handoffParams.append('affiliatecode', affiliateCodeParam)
      }
      if (affiliateNameParam) {
        if (handoffParams.get('affiliatename')) {
          handoffParams.delete('affiliatename')
        }
        handoffParams.append('affiliatename', affiliateNameParam)
      }
      forgetCart()
      window.location.href = `${PROPERTYLENS_APP_URL}?${handoffParams.toString()}`
    } catch (error) {
      Sentry.captureException(error)
      forgetCart()
      window.location.href = PROPERTYLENS_APP_URL
    }
  }

  useEffect(() => {
    const updateCart = async (sku) => {
      if (
        !sku ||
        cart?.lineItems?.find((lineItem) => lineItem.variant.sku === sku)
      ) {
        await handleCheckout()
        return
      }

      setApiError(false)
      setIsCartLoading(true)

      try {
        // Handle Race condition
        // Chord notified us that moving items into a cart order with a createdAt date older than 30 days
        // creates a race condition where chord returns an error response. We handle this by calling forgetCart
        // which triggers a new order to be created.
        const orderCreationDate = cart?.createdAt
        const diffDays = getTimeDifferenceInDays(orderCreationDate)
        if (diffDays > 29) {
          await forgetCart()
        } else {
          await emptyCart()
        }

        await addToCart({
          quantity: 1,
          sku,
          product: { variant: variant },
        })
        if (
          channel !== CHORD_OMS_DEFAULT_CHANNEL &&
          CHORD_OMS_DEFAULT_CHANNEL
        ) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - Added by Gus @ Chord
          // This will send a PATCH request right after adding the product to the cart.
          // You can watch the Network tab and verify the payload in that request's response.
          // You can also find and check the incomplete order in the OMS
          await modifyCart({
            // @ts-ignore
            attributes: { channel: CHORD_OMS_DEFAULT_CHANNEL },
          })
        }

        await handleCheckout()
      } catch (error) {
        Sentry.captureException(error)
        setApiError(translate('error_api_default'))
      }

      setIsCartLoading(false)
    }
    if (selectedSku) {
      updateCart(selectedSku)
    }
  }, [addToCart, emptyCart, selectedSku, translate])

  useEffect(() => {
    if (!parseInt(total)) return

    setPrice(total)
    setQuantity(
      lineItems.reduce((count, lineItem) => {
        return (
          count +
          parseInt(
            lineItem.variant.optionValues?.find(
              // @ts-ignore
              (optionValue) => optionValue.optionTypeName === 'report quantity'
            )?.presentation
          )
        )
      }, 0)
    )
  }, [lineItems, total])

  useEffect(() => {
    const variant = variantOptions?.[0]
    if (variant) {
      trackProductViewed({ product: { variant: variant } })
    }
  }, []) // eslint-disable-line

  const handleExistingUserLink = () => {
    window.location.href = `${process.env.NEXT_PUBLIC_IN_APP_DOMAIN}`
  }

  return (
    <>
      <Box
        sx={{
          borderRadius: showHeaderCart ? '0' : '24px 24px 0 0',
          overflow: 'hidden',
        }}
      >
        {showHeaderCart && (
          <CardHeader
            title={`Report${quantity > 1 ? 's' : ''} in cart`}
            quantity={quantity}
            description={null}
            price={price && toUsdCurrency(price)}
          />
        )}
        {(variantOptions || isPreviewing) && (
          <CustomCard>
            <Box>
              {showProductDescription && (
                <Box>
                  <div
                    dangerouslySetInnerHTML={{ __html: customProductText }}
                  />
                  <Box className="p-2"></Box>
                </Box>
              )}
              <Box>
                <span className="font-semibold">{bundleText}</span>
                <Box className="p-1 border-b-pl-dark border-b-1"></Box>
              </Box>

              <VariantOptions
                isLoading={isCartLoading}
                options={variantOptions}
                customRadioLabel={customRadioLabel}
                setSelectedSku={(newSku) => {
                  setSelectedSku(newSku)
                  setInputPromoCodeValue('')
                }}
              />
              <div className="flex">
                {/* @ts-ignore*/}
                {showPromoCode && (
                  <>
                    {/* @ts-ignore*/}
                    <Input
                      type="text"
                      label="Coupon Code"
                      placeholder="Have a promo code? Enter it here."
                      onChange={(e) => {
                        const tempValue = e.target.value
                        setInputPromoCodeValue(tempValue.toLowerCase())
                      }}
                      value={inputPromoCodeValue}
                      color="primary"
                      variant="bordered"
                      radius="none"
                      isDisabled={promoCodeInputDisabled}
                      classNames={{
                        inputWrapper: 'rounded-l-lg',
                      }}
                    />
                    <Button
                      radius="sm"
                      type="button"
                      color="primary"
                      onPress={() => {
                        if (inputPromoCodeValue) {
                          setIsCouponCodeLoading(true)
                        }
                        handleCheckout()
                      }}
                      isDisabled={isCouponCodeLoading || promoCodeInputDisabled}
                      size="lg"
                      aria-busy={isCouponCodeLoading}
                      aria-label="Apply"
                      className="relative rounded-none h-auto"
                    >
                      <Text
                        as="span"
                        sx={{ color: isCouponCodeLoading && 'transparent' }}
                      >
                        Apply
                      </Text>
                      {isCouponCodeLoading && (
                        <Flex
                          sx={{
                            alignItems: 'center',
                            bottom: 0,
                            left: 0,
                            justifyContent: 'center',
                            position: 'absolute',
                            right: 0,
                            top: 0,
                          }}
                        >
                          <Spinner
                            data-testid="spinner"
                            size={24}
                            color="inherit"
                          />
                        </Flex>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </Box>

            <Box my={3}>
              {apiError && (
                <Text
                  as="p"
                  color="errorDark"
                  variant="body"
                  mb={3}
                  sx={{ textAlign: 'center' }}
                >
                  <strong>{apiError}</strong>
                </Text>
              )}
              {isCartLoading && (
                <div className="relative">
                  <Flex
                    sx={{
                      alignItems: 'center',
                      bottom: 0,
                      left: 0,
                      justifyContent: 'center',
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      height: '300px',
                      background: 'white',
                    }}
                  >
                    <Spinner data-testid="spinner" size={48} color="inherit" />
                  </Flex>
                </div>
              )}
              <div
                ref={paymentElementRef}
                id="checkout"
                style={{
                  visibility: isCartLoading ? 'hidden' : 'visible',
                }}
              ></div>
            </Box>
            {checkout && klaviyoSampleFormId && (
              <div
                sx={{
                  marginBottom: '16px',
                  color: 'black',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                Not ready to buy?
                <KlaviyoFormModal
                  klaviyoSampleFormCtaText={klaviyoSampleFormCtaText}
                  klaviyoSampleFormId={klaviyoSampleFormId}
                />
              </div>
            )}
            <div sx={{ color: 'black', textAlign: 'center' }}>
              Already have an account?{' '}
              <span
                onClick={handleExistingUserLink}
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Log In
              </span>
            </div>
          </CustomCard>
        )}
      </Box>
    </>
  )
}

export default CheckoutCheckoutForm
